import React, { useState, lazy } from 'react';
import { Link } from 'react-router-dom';
import { ScrollSection } from '../components/About/ScrollSection';
import { Tabs, TabList, Tab, TabPanel } from '../components/UI/Tabs';
import { Button } from '../components/UI/Button';
import { ArrowRightIcon, ArrowRight } from 'lucide-react';
export const About = () => {
  const [activeTab, setActiveTab] = useState('vision');
  const teamMembers = [{
    id: 'andrew-igbineweka',
    name: 'Andrew Igbineweka',
    role: 'Founder',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/2e91tcJdXJukNhGR3YoR4c/Andrew_Igbineweka%2C_Founder_.jpg',
    brief: "Andrew moves through the world with the steadiness of someone who understands that ideas only matter when they are built into systems that change people's lives. As a Learning Experience Designer and Digital Learning Strategist, he has spent more than seven years building learning ecosystems that help individuals and organizations adapt to a world increasingly defined by AI and digital innovation."
  }, {
    id: 'precious-obayuwana',
    name: 'Precious Obayuwana',
    role: 'Chief Operations Officer',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/tiuzezKDFtcTj9dbiEA2Tq/Precious_Obayuwana%2C_Chief_Operations_Officer.jpg',
    brief: 'Precious brings a rare kind of operational clarity—the kind that steadies an organization, sharpens its rhythm, and turns vision into grounded, repeatable excellence. Her work sits at the heart of systems, people, and purpose, yet her impact is felt in the quiet confidence she brings into every room: things will work, people will grow, and the mission will move.'
  }, {
    id: 'bibi-eneje',
    name: 'Bibi Eneje',
    role: 'Chief Growth Officer',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/7u76HdqRGggPRPNySjG8aG/Bibi_Eneje%2C_Chief_Growth_Officer.png',
    brief: 'Bibi enters a room the way a catalyst enters a reaction—subtle at first, then quietly transformative. Her work sits at the crossroads of storytelling, culture shaping, and strategic influence, and she carries the rare ability to turn scattered potential into structured momentum. Growth, in her hands, is not a department. It is an ecosystem.'
  }, {
    id: 'bella-ojoka',
    name: 'Bella Ojoka',
    role: 'Programs and Community Lead',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/vy2d386bQZ37DRZ67avafS/Bella_Ojoka%2C_Programs_and_Community_Lead.jpg',
    brief: "Bella carries a kind of steadiness that makes complex work feel possible. Her mind is analytical, her heart is people-centered, and her craft lives in the space where data, strategy, and human transformation meet. She is the kind of leader who doesn't just run programs—she architects experiences that move people forward."
  }, {
    id: 'george-ozigagu-topa',
    name: 'George Ozigagu Topa',
    role: 'Human Resources Lead',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/7NPfEGqqSwbJWuP8w8bnhB/George_Ozigagu_Topa%2C_Human_Resources.jpg',
    brief: 'George is the kind of HR leader every growing ecosystem needs—measured, intuitive, and deeply committed to the science and soul of people. He understands that culture is not created by accident; it is engineered through clarity, trust, and the right talent in the right roles.'
  }, {
    id: 'maxwell-orioko',
    name: 'Maxwell Orioko',
    role: 'Content & Design Lead',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/ted3p6u8tzPPN9kZoNtuEk/Maxwell_Orioko%2C_Content_and_Design_Lead.jpg',
    brief: 'Maxwell is the visual mind of the ecosystem—the one who turns ideas into identity, concepts into form, and strategy into imagery that lingers long after the room has emptied. His work threads together graphic design, digital storytelling, and AI-powered visual creation, giving The Networking Salon its unmistakable aesthetic rhythm: sharp, intentional, and deeply human.'
  }];
  return <div className="pt-20" data-id="element-1418">
      <section className="py-16 bg-secondary/5" data-id="element-1419">
        <div className="container mx-auto px-4" data-id="element-1420">
          <h1 className="text-4xl md:text-5xl font-bold text-center text-secondary-dark mb-8" data-id="element-1421">
            About The Networking Salon
          </h1>
          <p className="text-center text-lg text-secondary max-w-3xl mx-auto" data-id="element-1422">
            Building communities that connect professionals to powerful
            networks, transformative ideas, and life-changing opportunities.
          </p>
        </div>
      </section>
      <div className="container mx-auto px-4 py-16" data-id="element-1423">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-24" data-id="element-1424">
          <div className="order-2 lg:order-1" data-id="element-1425">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pmaGVbLENs5yRHebja1Kb2/3_Bibi.jpg" alt="Diverse people networking at The Networking Salon" className="w-full h-auto rounded-lg shadow-md grayscale" loading="lazy" style={{
            maxWidth: '100%'
          }} data-id="element-1426" />
          </div>
          <div className="order-1 lg:order-2" data-id="element-1427">
            <h2 className="text-3xl font-bold mb-6 text-secondary-dark" data-id="element-1428">
              About Us
            </h2>
            <p className="text-secondary text-lg" data-id="element-1429">
              The Networking Salon is a global initiative designed to connect
              young professionals, coaches, mentors, business leaders,
              executives, and social impact drivers (ages 18–45) across 30
              cities worldwide. We build close-knit, high-value communities that
              connect people to powerful networks, transformative ideas, and
              accessible opportunities that help them thrive in their careers,
              businesses, and impact initiatives.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-24" data-id="element-1430">
          <div data-id="element-1431">
            <h2 className="text-3xl font-bold mb-6 text-secondary-dark" data-id="element-1432">
              Why We Exist
            </h2>
            <p className="text-secondary text-lg" data-id="element-1433">
              Today's professionals face increasing isolation, fragmented
              networks, and limited access to life-changing opportunities. In
              many cities, young changemakers and professionals lack trusted
              communities where they can find both strategic connections and
              meaningful collaboration. Without intentional networking
              platforms, talent and ideas remain siloed. Promising leaders are
              left without the visibility, opportunities, or relationships they
              need to grow and scale their work.
            </p>
          </div>
          <div data-id="element-1434">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3oN7mV8GW4b1q39kgBjUNn/1755202597036.jpg" alt="Professionals facing isolation and fragmented networks" className="w-full h-auto rounded-lg shadow-md grayscale" loading="lazy" style={{
            maxWidth: '100%'
          }} data-id="element-1435" />
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-24" data-id="element-1436">
          <div className="order-2 lg:order-1" data-id="element-1437">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/cMappWzpJqsfBegZChWrww/1755202597051.jpg" alt="Salon-style gatherings and collaborative sessions" className="w-full h-auto rounded-lg shadow-md grayscale" loading="lazy" style={{
            maxWidth: '100%'
          }} data-id="element-1438" />
          </div>
          <div className="order-1 lg:order-2" data-id="element-1439">
            <h2 className="text-3xl font-bold mb-6 text-secondary-dark" data-id="element-1440">
              Our Solution
            </h2>
            <p className="text-secondary text-lg" data-id="element-1441">
              The Networking Salon is more than a meetup — it's a vibrant,
              human-centered community. Through salon-style gatherings,
              mastermind sessions, micro-events, and digital collaboration
              spaces, members build trusted relationships, gain visibility,
              access curated opportunities (grants, fellowships, speaking
              engagements, career openings), exchange powerful ideas, and unlock
              their potential in a dynamic, supportive environment.
            </p>
            <p className="text-secondary text-lg mt-4" data-id="element-1442">
              We've designed The Networking Salon as a layered ecosystem—free at
              the edges, valuable at the core, and transformative at the summit.
              Each level is a step closer to intentional growth, meaningful
              connection, and collaborative impact.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-24" data-id="element-1443">
          <div data-id="element-1444">
            <h2 className="text-3xl font-bold mb-6 text-secondary-dark" data-id="element-1445">
              Community Structure
            </h2>
            <div className="space-y-4" data-id="element-1446">
              <div className="border-l-2 border-gray-300 pl-4" data-id="element-1447">
                <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1448">
                  The Waiting Room
                </h3>
                <p className="text-gray-600" data-id="element-1449">
                  An open, welcoming space for curious minds and aspiring
                  community members, providing free entry into our digital
                  ecosystem.
                </p>
              </div>
              <div className="border-l-2 border-gray-300 pl-4" data-id="element-1450">
                <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1451">
                  Event Pathway
                </h3>
                <p className="text-gray-600" data-id="element-1452">
                  Paid access through in-person participation, ideal for
                  professionals who thrive in face-to-face environments.
                </p>
              </div>
              <div className="border-l-2 border-gray-300 pl-4" data-id="element-1453">
                <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1454">
                  Inner Circle
                </h3>
                <p className="text-gray-600" data-id="element-1455">
                  Full community membership with access to masterclasses,
                  resources, and networking with coaches and business leaders.
                </p>
              </div>
              <div className="border-l-2 border-gray-300 pl-4" data-id="element-1456">
                <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1457">
                  Lifetime Partner Access
                </h3>
                <p className="text-gray-600" data-id="element-1458">
                  Strategic & personalized growth for entrepreneurs seeking deep
                  transformation and tailored support.
                </p>
              </div>
            </div>
          </div>
          <div data-id="element-1459">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/gLvZ6fTjQSpPs86zUNEWZz/Group.jpg" alt="The Networking Salon community structure" className="w-full h-auto rounded-lg shadow-md grayscale" loading="lazy" style={{
            maxWidth: '100%'
          }} data-id="element-1460" />
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" id="vision-mission-values" data-id="element-1461">
          <div data-id="element-1462">
            <h2 className="text-3xl font-bold mb-6 text-secondary-dark" data-id="element-1463">
              Our Vision, Mission & Core Values
            </h2>
            <div data-id="element-1464">
              <Tabs defaultTab="vision" className="mt-4" data-id="element-1465">
                <TabList data-id="element-1466">
                  <Tab id="vision" data-id="element-1467">Vision</Tab>
                  <Tab id="mission" data-id="element-1468">Mission</Tab>
                  <Tab id="values" data-id="element-1469">Values</Tab>
                </TabList>
                <TabPanel id="vision" data-id="element-1470">
                  <div className="p-4 border border-gray-200 rounded-lg mt-4" data-id="element-1471">
                    <h3 className="text-xl font-bold mb-2" data-id="element-1472">Our Vision</h3>
                    <p data-id="element-1473">
                      A world where every young professional and changemaker has
                      access to powerful networks, transformative ideas, and
                      life-changing opportunities — regardless of where they
                      start.
                    </p>
                  </div>
                </TabPanel>
                <TabPanel id="mission" data-id="element-1474">
                  <div className="p-4 border border-gray-200 rounded-lg mt-4" data-id="element-1475">
                    <h3 className="text-xl font-bold mb-2" data-id="element-1476">Our Mission</h3>
                    <p data-id="element-1477">
                      To build thriving, close-knit communities across 30 cities
                      by 2030 that connect professionals, mentors, and leaders
                      to people, ideas, and opportunities that help them grow,
                      lead, and thrive.
                    </p>
                  </div>
                </TabPanel>
                <TabPanel id="values" data-id="element-1478">
                  <div className="p-4 border border-gray-200 rounded-lg mt-4" data-id="element-1479">
                    <h3 className="text-xl font-bold mb-2" data-id="element-1480">Core Values</h3>
                    <ul className="list-disc list-inside space-y-1" data-id="element-1481">
                      <li data-id="element-1482">Connection with Purpose</li>
                      <li data-id="element-1483">Opportunity for All</li>
                      <li data-id="element-1484">Community over Competition</li>
                      <li data-id="element-1485">Excellence and Impact</li>
                      <li data-id="element-1486">Global Mindset with Local Action</li>
                      <li data-id="element-1487">Trust and Respect</li>
                    </ul>
                  </div>
                </TabPanel>
              </Tabs>
            </div>
          </div>
          <div data-id="element-1488">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/gLvZ6fTjQSpPs86zUNEWZz/Group.jpg" alt="Global community of professionals and changemakers" className="w-full h-auto rounded-lg shadow-md grayscale" loading="lazy" style={{
            maxWidth: '100%'
          }} data-id="element-1489" />
          </div>
        </div>
      </div>

      {/* The Team Section */}
      <section className="py-20 bg-gray-50 border-t border-gray-200" data-id="element-1490">
        <div className="container mx-auto px-4" data-id="element-1491">
          <div className="text-center mb-14" data-id="element-1492">
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-dark mb-4" data-id="element-1493">
              Meet The Team
            </h2>
            <p className="text-lg text-secondary max-w-2xl mx-auto" data-id="element-1494">
              The passionate people behind The Networking Salon — curators,
              trainers, and community builders dedicated to connecting
              professionals across the globe.
            </p>
          </div>

          <div className="max-w-6xl mx-auto space-y-16 mb-16" data-id="element-1495">
            {teamMembers.map((member, index) => {
            const isEven = index % 2 === 0;
            return <div key={member.id} className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center ${isEven ? '' : 'lg:grid-flow-dense'}`} data-id="element-1496">
                  {/* Image */}
                  <div className={`${isEven ? '' : 'lg:col-start-2'} flex justify-center`} data-id="element-1497">
                    <div className="relative w-64 h-64 md:w-80 md:h-80" data-id="element-1498">
                      <div className="absolute inset-0 border-2 border-gray-300 rounded-full" data-id="element-1499"></div>
                      <img src={member.image} alt={member.name} className="w-full h-full rounded-full object-cover grayscale hover:grayscale-0 transition-all duration-300" data-id="element-1500" />
                    </div>
                  </div>
                  {/* Content */}
                  <div className={`${isEven ? '' : 'lg:col-start-1 lg:row-start-1'} ${isEven ? 'lg:text-left' : 'lg:text-right'}`} data-id="element-1501">
                    <h3 className="text-3xl font-bold text-gray-800 mb-2" data-id="element-1502">
                      {member.name}
                    </h3>
                    <p className="text-xl text-gray-600 font-medium mb-6" data-id="element-1503">
                      {member.role}
                    </p>
                    <p className="text-gray-700 mb-6 leading-relaxed" data-id="element-1504">
                      {member.brief}
                    </p>
                    <Link to={`/team/${member.id}`} className={`inline-flex items-center gap-2 px-6 py-3 border-2 border-gray-800 rounded-md text-gray-800 font-medium hover:bg-gray-800 hover:text-white transition-colors duration-300 ${isEven ? '' : 'lg:flex-row-reverse'}`} data-id="element-1505">
                      <span data-id="element-1506">Learn More</span>
                      <ArrowRight size={16} data-id="element-1507" />
                    </Link>
                  </div>
                </div>;
          })}
          </div>

          {/* Join Team CTA */}
          <div className="max-w-3xl mx-auto text-center pt-12 border-t border-gray-200" data-id="element-1508">
            <h3 className="text-2xl font-bold text-gray-800 mb-4" data-id="element-1509">
              Join Our Team
            </h3>
            <p className="text-gray-600 mb-8" data-id="element-1510">
              We're always looking for passionate individuals to join our
              mission. Explore opportunities to contribute to our growing
              community.
            </p>
            <Link to="/join-team" className="inline-flex items-center justify-center px-6 py-3 border-2 border-gray-800 rounded-md text-gray-800 font-medium hover:bg-gray-800 hover:text-white transition-colors duration-300" data-id="element-1511">
              View Open Positions
              <ArrowRight size={16} className="ml-2" data-id="element-1512" />
            </Link>
          </div>
        </div>
      </section>
    </div>;
};